// The entry form's frame, Astra's shape (batch 3 · sub-broker and cheque; operator's ruling 26 Oct 2026: "simplified or
// closer to that version"). One card holding the fields on a two-column grid, a hairline, then the foot: one muted line
// at the left (the one thing still missing, when something is) and the one gold primary at the right. Beside it from lg,
// the review once it is asked for, and About this job (the explanation, the desk and when the books were read); no
// standing "Before confirming" block (Astra, 1 Nov 2026). On the phone the review sits inside the card, above the foot, so
// the readback is always just above the press, and About this job folds under the card.
import type { ReactNode } from 'react';
import { AboutFold } from '../record/parts';
import type { TreasuryRow } from '../../../lib/api';
import type { RailValue } from '../../RailPicker';
import { railName, sameAsset } from '../../../lib/naming';

/** A picked account's name as the picker says it — the row's stored label when the accounts were read. */
export function railWord(rows: readonly TreasuryRow[], v: RailValue): string {
  if (!v.slug) return '';
  return railName(rows.find((r) => r.slug === v.slug && sameAsset(r.asset, v.asset)) ?? { slug: v.slug });
}

/** The two-column grid of a form card. A field that takes the whole line on the phone passes `max-lg:col-span-2`. */
export function FormGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-x-3 gap-y-4 lg:gap-x-4">{children}</div>;
}

/** A group's caption inside the card (Astra: "Desk agreement", "Client settlement"). */
export function FormGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-label font-medium uppercase tracking-wider text-tg-hint">{title}</h2>
      <FormGrid>{children}</FormGrid>
    </section>
  );
}

/** The card: the fields, then (phone, once asked) the review, the answer lines and the foot. */
export function FormCard({ children, review, answers, note, primary }: {
  children: ReactNode;
  /** Drawn here below lg only; from lg it is the aside. */
  review?: ReactNode;
  /** Every answer opens above the button (§7 rule 11): progress, refusals, the repeat panel, the receipt. */
  answers?: ReactNode;
  /** The foot's one line: what is still missing (nothing when nothing is). */
  note?: ReactNode;
  primary: ReactNode;
}) {
  return (
    <section className="rounded-[10px] border border-stroke bg-panel p-4 lg:p-6">
      <div className="space-y-5">{children}</div>
      {review && <div className="mt-5 border-t border-stroke-soft pt-4 xl:hidden">{review}</div>}
      {answers && <div className="mt-5 space-y-2">{answers}</div>}
      <div className="mt-5 flex flex-col-reverse items-stretch gap-3 border-t border-stroke-soft pt-5 lg:flex-row lg:items-center lg:justify-between">
        {note ? <p className="text-center text-meta text-tg-hint lg:text-left">{note}</p> : <span className="max-lg:hidden" />}
        {primary}
      </div>
    </section>
  );
}

/** Beside the card, from lg: the review once he asked for it, then About this job. */
export function BeforeConfirming({ review, help, facts }: {
  review?: ReactNode;
  /** The explanation, behind a tap. */
  help: ReactNode;
  /** The desk and when the books this form stands on were read ("12:20"), or — when they were not. */
  facts: ReadonlyArray<{ label: string; value: ReactNode }>;
}) {
  return (
    <aside className="hidden min-w-0 space-y-6 xl:block xl:pt-2">
      {review && (
        <div>
          <h2 className="text-title font-semibold">Before confirming</h2>
          <div className="mt-4">{review}</div>
        </div>
      )}
      <AboutFold help={help} facts={facts} />
    </aside>
  );
}

/** The page: the card, and its column beside it from lg; `below` sits under the card on the phone (About this job). */
export function FormPage({ card, aside, below }: { card: ReactNode; aside: ReactNode; below?: ReactNode }) {
  // No column beside the form when it would hold nothing but the folded explanation (1 Nov 2026 audit, P7): then `below`
  // shows at every width and the form keeps its 600 px.
  if (!aside) return <div className="grid items-start gap-6 xl:max-w-[600px]">{card}{below && <div className="-mt-2">{below}</div>}</div>;
  return (
    <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,600px)_minmax(280px,1fr)]">
      {card}{aside}{below && <div className="-mt-2 xl:hidden">{below}</div>}
    </div>
  );
}
